/* eslint-disable no-unused-vars */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { gql, useMutation, useQuery } from '@apollo/client';
import GetAppIcon from '@mui/icons-material/GetApp';

import Link from '@atoms/Link';
import Skeleton from '@atoms/Skeleton';
import Menu from '@molecules/Menu';
import MutationConfirmationDialog from '@molecules/MutationConfirmationDialog';
import moment from 'moment';
import { useTranslation } from '@app/config/i18n';
import { useFileDownload } from '@app/helpers/hooks';
import { SERVER_PUBLIC_URL, EXPORT_TYPES } from '@app/constants';
import { CircularProgress } from '@mui/material';
// import Button from '@app/components/atoms/Button';


const EXPORT_OPPORTUNITY_QUERY = gql`
	query getExportedOpportunity($opportunitySlug: String!) {
		exportedOpportunity(opportunitySlug: $opportunitySlug) {
			filename
			download_url
			is_ready
			version_date
		}
	}
`;

const EXPORT_OPPORTUNITY_MUTATION = gql`
	mutation createOpportunityExport($opportunitySlug: String!) {
		createOpportunityExport(opportunitySlug: $opportunitySlug) {
			filename
			download_url
			is_ready
			version_date
		}
	}
`;

const EXPORT_SUBMISSION_QUERY = gql`
	query exportedOpportunitySubmissions($opportunitySlug: String!) {
		exportedOpportunitySubmissions(opportunitySlug: $opportunitySlug) {
			filename
			download_url
			is_ready
			version_date
		}
	}
`;

const EXPORT_SUBMISSION_MUTATION = gql`
	mutation createOpportunitySubmissionsExport($opportunitySlug: String!) {
		createOpportunitySubmissionsExport(opportunitySlug: $opportunitySlug) {
			filename
			download_url
			is_ready
			version_date
		}
	}
`;

const EXPORT_FEEDBACK_QUERY = gql`
	query exportedOpportunityFeedbackData($opportunitySlug: String!) {
		exportedOpportunityFeedbackData(opportunitySlug: $opportunitySlug) {
			filename
			download_url
			is_ready
			version_date
		}
	}
`;

const EXPORT_FEEDBACK_MUTATION = gql`
	mutation createOpportunitySubmissionsExport($opportunitySlug: String!) {
		createOpportunitySubmissionsExport(opportunitySlug: $opportunitySlug) {
			filename
			download_url
			is_ready
			version_date
		}
	}
`;

function ApplicationsExport({ opportunitySlug, exportType, exportParams }) {
	const { t } = useTranslation('dashboard');
	let opportunityFileURL;
	
	if (exportType === EXPORT_TYPES.SUBMISSION) {
		opportunityFileURL = `${SERVER_PUBLIC_URL}/api/services/export/opportunity/submission/${opportunitySlug}/xlsx?${exportParams.toString()}`;
	} else if (exportType === EXPORT_TYPES.FEEDBACK) {
		opportunityFileURL = `${SERVER_PUBLIC_URL}/api/services/export/opportunity/feedback/${opportunitySlug}/xlsx?${exportParams.toString()}`;
	} else {
		opportunityFileURL = `${SERVER_PUBLIC_URL}/api/services/export/opportunity/${opportunitySlug}/xlsx?${exportParams.toString()}`;
	}
	const [anchorEl, setAnchorEl] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [
		downloadFile, 
		{loading: downloadLoading, error: DownloadError}
	] = useFileDownload(opportunityFileURL);

	const {
		loading: queryLoading,
		data: {
			exportedOpportunity = {}
		} = {}
	} = useQuery(exportType === EXPORT_TYPES.SUBMISSION ? EXPORT_SUBMISSION_QUERY : EXPORT_OPPORTUNITY_QUERY, {
		variables: {
			opportunitySlug
		}
	});

	const [
		exportOpportunityMutation,
		{ loading: mutationLoading, error: mutationError }
	] = useMutation(exportType === EXPORT_TYPES.SUBMISSION ? EXPORT_SUBMISSION_MUTATION : EXPORT_OPPORTUNITY_MUTATION , {
		variables: {
			opportunitySlug
		},
		onCompleted() {
			setDialogOpen(false);
		},
		update(cache, { data: {createOpportunityExport} }) {
			cache.writeQuery({
			  	query: exportType === EXPORT_TYPES.SUBMISSION ? EXPORT_SUBMISSION_QUERY : EXPORT_OPPORTUNITY_QUERY,
			  	data: {
					exportedOpportunity: createOpportunityExport
			  	},
				variables: {
					opportunitySlug
				}
			});
		}
	});

	const downloadExcelData = () => {
		downloadFile();
	}

	const handleDialogOpen = (setOpen) => () => setDialogOpen(setOpen);
	
	const handleClick = (event) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);
	const handleCloseWithDialog = () => {
		setDialogOpen(true);
		setAnchorEl(null)
	};


    let exportDataComponent = <Skeleton height="24px" />
	let disableVersion = true;
    if ( !queryLoading) {
		if (exportedOpportunity && exportedOpportunity.is_ready) {
			disableVersion = false;
			exportDataComponent = (
				<Link
					href={exportedOpportunity.download_url}
					target="_blank"
					rel="noopener noreferrer"
					download>
					{t('export.download', {version: moment(exportedOpportunity.version_date).format('DD-MMM-YYYY')})}
				</Link>
			);
		} else {			
			exportDataComponent = t('export.empty');
		}
    }
    
	return (
		<>
			{/* <Button
				color="primary"
				aria-controls="export-menu"
				aria-haspopup="true"
				variant="text"
				onClick={handleClick}
				startIcon={<GetAppIcon />}
				style={{ padding: '0.5rem' }}
			>
				Export
			</Button> */}
			<GetAppIcon 
				color="primary"
				aria-controls="export-menu"
				aria-haspopup="true"
				onClick={handleClick}
			/>
			<Menu
				id="export-menu"
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				<Menu.Item onClick={handleCloseWithDialog}>
					{t('export.generate')}
				</Menu.Item>
				<Menu.Item disabled={disableVersion}>
					{ exportDataComponent }
				</Menu.Item>
				<Menu.Item onClick={downloadExcelData} disabled={downloadLoading}>
					{t('export.excel')} &nbsp;
					{downloadLoading && <CircularProgress size={15} />}
				</Menu.Item>
			</Menu>
			<MutationConfirmationDialog
				title={t('export.title')}
				loading={mutationLoading}
				error={mutationError}
				isOpen={dialogOpen}
				handleClose={handleDialogOpen(false)}
				handleConfirm={exportOpportunityMutation}
				>
				{t('export.confirmation')}
			</MutationConfirmationDialog>
		</>
	)
}

ApplicationsExport.propTypes = {
	opportunitySlug: PropTypes.string.isRequired,
	exportType: PropTypes.string.isRequired,
	exportParams: PropTypes.object
};


ApplicationsExport.defaultProps = {
  exportParams: {},
};

export default ApplicationsExport
